import { useState, useMemo } from "react";
import { Card, Input, InputGroup, Button, Drawer, Avatar, useOverlayState, toast } from "@heroui/react";
import { useSession } from "@clerk/react";
import { createUser as createUserApi } from "@/api/users";

const DesktopCreateMemberDrawer = ({ show = false, onResp = () => {} }) => {
  const { session } = useSession();

  const dummyName = "John Snow";
  const dummyMail = "js@starks.thenorth";

  const [creating, setCreating] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileZa, setMobileZa] = useState("");

  const [touched, setTouched] = useState({ first: false, last: false });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const state = useOverlayState({
    isOpen: show,
    onOpenChange: (isOpen) => {
      if (!isOpen) onResp("close", null);
    },
  });

  const markTouched = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const handleMobileChange = (val) => {
    const digits = (val || "").replace(/\D/g, "");
    setMobileZa(digits.slice(0, 10));
  };
  const mobileDisplay = useMemo(() => mobileZa, [mobileZa]);

  const avatarName = firstName || lastName ? `${firstName} ${lastName}`.trim() : dummyName;
  const rawSeed = firstName || lastName ? `${firstName}${lastName}` : dummyMail;
  const avatarSeed = encodeURIComponent(rawSeed);

  const firstInvalid = firstName.trim() === "";
  const lastInvalid = lastName.trim() === "";
  const isValid = !firstInvalid && !lastInvalid;

  const showFirstError = (touched.first || submitAttempted) && firstInvalid;
  const showLastError = (touched.last || submitAttempted) && lastInvalid;

  async function createMember() {
    setSubmitAttempted(true);
    if (!isValid) return;

    const token = await session.getToken();
    const national = (mobileZa || "").replace(/\D/g, "").replace(/^0/, "");
    const mobileFull = national ? `+27${national}` : "";

    const params = {
      first_name: firstName?.trim(),
      last_name: lastName?.trim(),
      email: email?.trim(),
      mobile: mobileFull,
    };

    setCreating(true);
    createUserApi(params, token)
      .then((_resp) => {
        if (_resp?.data?.Result === "SUCCESS") {
          toast.success("Participant Created");
          onResp("close", null);
        } else {
          toast.danger("Could not add member", {
            description: "Please check the details and try again.",
          });
          console.error("Create user failed:", _resp?.data);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.danger("Error", {
          description: "Something went wrong while creating the member.",
        });
      })
      .finally(() => setCreating(false));
  }

  const inputClass =
    "bg-white dark:bg-[#2C2C2C] border border-black/[0.06] hover:bg-surface-secondary rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-none text-foreground placeholder:text-muted";
  const labelClass = "text-xs font-medium tracking-wide text-foreground";

  return (
    <Drawer state={state}>
      <Drawer.Backdrop variant="blur" className="bg-black/30">
        <Drawer.Content placement="right">
          <Drawer.Dialog className="rounded-b-0 dark:bg-black pb-16">
        
            <Drawer.Header className="flex items-start gap-3">
              <Drawer.Heading className="flex flex-col gap-3">
                <div className="flex flex-col text-4xl font-black leading-[38px]">
                  <span className="text-accent/60 dark:text-white">Get them</span>
                  <span className="text-accent">in the Loopp</span>
                </div>

                <span className="text-sm text-muted">Invite other participants to stay connected.</span>
              </Drawer.Heading>
            </Drawer.Header>

            <Drawer.Body className="pb-6 flex flex-col gap-4 relative z-10">
              {/* First Name */}
              <div className="flex flex-col gap-2">
                <label className={labelClass}>First Name <span className="text-danger text-sm">*</span></label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={() => markTouched("first")}
                  type="text"
                  placeholder={dummyName.split(" ")[0]}
                  className={inputClass}
                />
                {showFirstError && <p className="text-xs text-danger">First name is required</p>}
              </div>

              {/* Last Name */}
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Last Name <span className="text-danger text-sm">*</span></label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={() => markTouched("last")}
                  type="text"
                  placeholder={dummyName.split(" ")[1]}
                  className={inputClass}
                />
                {showLastError && <p className="text-xs text-danger">Last name is required</p>}
              </div>

              {/* Email */}
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Email (optional)</label>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder={dummyMail}
                  className={inputClass}
                />
                <p className="text-xs text-muted">
                  Optional — but recommended for notifications.
                </p>
              </div>

              {/* Mobile */}
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Mobile (optional)</label>
                <InputGroup className={inputClass}>
                  <InputGroup.Prefix>
                    <span className="text-muted text-sm select-none px-1">+27</span>
                  </InputGroup.Prefix>
                  <InputGroup.Input
                    value={mobileDisplay}
                    onChange={(e) => handleMobileChange(e.target.value)}
                    type="tel"
                    placeholder="82 123 4567"
                    // className={inputClass}
                  />
                </InputGroup>
              </div>
            </Drawer.Body>

            <Drawer.Footer className="gap-3 relative z-10">
              <Button onPress={() => onResp("close", null)} className="w-1/3 bg-background text-foreground" isDisabled={creating}>
                Cancel
              </Button>
              <Button
                onPress={createMember}
                isPending={creating}
                isDisabled={creating}
                className="w-full disabled:opacity-100"
                variant="primary"
              >
                Create
              </Button>
            </Drawer.Footer>

            {/* <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 drawer-blob" /> */}
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
};

export default DesktopCreateMemberDrawer;
